import { Component } from 'react';
import PropTypes from 'prop-types';
import { getBuyerId, getUserType } from 'dibs-cookie-jar';
import { connect } from 'react-redux';
import { updateRelayVariables } from '../actions/sharedRelayVariableActions';
import { CUSTOMER_TYPE_TRADE, CUSTOMER_TYPE_VIP } from '../constants/userConstants';
import {
    getUserZipCode,
    getUserSessionCountryCode,
    getUserSessionRegions,
} from 'dibs-regional-info/exports/regionalInfoHelpers';
import { setUserRegionalInfo } from 'dibs-regional-info/exports/setUserRegionalInfo';
import serverVars from 'server-vars';

export class SharedUserAndRelayVarLoaderComponent extends Component {
    constructor() {
        super();

        this.userFetched = false;
    }

    async componentDidMount() {
        /**
         * Fetch the user if needed and refetch any fields that depend on `isClient`
         */
        await this.triggerRefetch();
    }

    async UNSAFE_componentWillReceiveProps(nextProps) {
        /**
         * First condition: allow for page refreshes from auction pdps
         * Second condition: For cases where the user logs in while browsing the page.
         */
        if (!this.props.updateAuctionItem && nextProps.updateAuctionItem) {
            await this.triggerRefetch();
        } else if (!this.props.fetchUser && nextProps.fetchUser && !this.userFetched) {
            const userType = getUserType(document.cookie);
            const isTrade = userType === CUSTOMER_TYPE_TRADE;
            // FINDING-13500: Need to refresh when logging-in as trade user so that
            // "ut" param is appended to url. Without "?ut=trade" in url, some of the
            // trade-specific features, such as filtering by net price, do not work
            if (isTrade && this.props.refreshPageOnTradeLogin) {
                window.location.reload();
            } else {
                await this.triggerRefetch();
            }
        } else if (!this.props.updateItemPricing && nextProps.updateItemPricing) {
            await this.triggerRefetch({ userCountryCode: nextProps.updatedUserCountryCode });
        }
    }

    async triggerRefetch(updatedVariables) {
        const variables = {
            ...(await this.getUserRelayVars()),
            ...(!!updatedVariables?.userCountryCode && {
                userCountryCode: updatedVariables.userCountryCode,
            }),
            isClient: true,
        };

        this.props.onUserRefetch(variables, null, (err, additionalVariables) => {
            /**
             * Mimick relay behaviour - only change the variables after a successful refetch.
             */
            if (err) {
                this.userFetched = false;
            } else if (this.props.shouldUpdateRelayVariables) {
                this.props.updateRelayVariables({
                    ...variables,
                    ...additionalVariables,
                    updateAuctionItem: false,
                    updateItemPricing: false,
                });
            }
        });

        this.userFetched = variables.fetchUser;
    }

    async getUserRelayVars() {
        /**
         * Must default to an empty string! Because the default value for userId in the
         * relay query is set to an empty string. Defaulting to anything different will ruin
         * your day, trust me ;)
         */
        const userId = getBuyerId(document.cookie) || '';
        const userType = getUserType(document.cookie);

        /**
         * check and set (if missing) user regional info in sessionStorage
         * this is needed when user opens a new tab, and sessionStorage is still empty
         */
        if (this.props.environment) {
            // await is needed here! It's needed to make sure to wait till
            // sessionStorage.userGeoInfo has enough data to continue
            // this function execution where userZipCode, userCountryCode and hasSessionRegions
            // is being set a few lines down
            await setUserRegionalInfo({
                relayEnv: this.props.environment,
                cookieDomain: serverVars.get('cookieDomain'),
                userId,
            });
        }
        const userZipCode = getUserZipCode();
        const userCountryCode = getUserSessionCountryCode();
        const hasSessionRegions = !!getUserSessionRegions();
        return {
            userId,
            fetchUser: !!userId,
            userIds: userId ? [userId] : [],
            fetchFollowSearch: !!userId,
            isTrade: userType === CUSTOMER_TYPE_TRADE,
            isVip: userType === CUSTOMER_TYPE_VIP,
            userZipCode,
            userCountryCode,
            fetchRegionalInfo: !hasSessionRegions,
        };
    }

    render() {
        return null;
    }
}

function mapStateToProps(state) {
    return {
        /**
         * updateUserState is used for responsive. The authentication is on the navigation is
         * managed in dbl, so we need to know when a user has successfully logged in/registered
         *
         * fetchUser is used for mobile. The mobile nav works a little bit differently from dbl.
         * It calls a callback when a user clicks an auth button and we handle all of the auth
         * flow in `app-buyer-finding`.
         *
         * TODO: Make sure they work the same way.
         * NOTE: updateUserState does not seem to exist, even for responsive it is using fetchUser
         */
        fetchUser: state.header.updateUserState || state.relayVariables.variables.fetchUser,
        updateAuctionItem: !!state.relayVariables.variables.updateAuctionItem,
        updateItemPricing: !!state.relayVariables.variables.updateItemPricing,
        updatedUserCountryCode: state.relayVariables.variables.updatedUserCountryCode,
    };
}

SharedUserAndRelayVarLoaderComponent.defaultProps = {
    shouldUpdateRelayVariables: true,
    refreshPageOnTradeLogin: true,
    updateAuctionItem: false,
    updateItemPricing: false,
    updatedUserCountryCode: '',
};

SharedUserAndRelayVarLoaderComponent.propTypes = {
    updateRelayVariables: PropTypes.func.isRequired,
    onUserRefetch: PropTypes.func.isRequired,
    environment: PropTypes.object,
    fetchUser: PropTypes.bool.isRequired,
    shouldUpdateRelayVariables: PropTypes.bool.isRequired,
    refreshPageOnTradeLogin: PropTypes.bool,
    updateAuctionItem: PropTypes.bool,
    updateItemPricing: PropTypes.bool,
    updatedUserCountryCode: PropTypes.string,
};

export const SharedUserAndRelayVarLoader = connect(mapStateToProps, { updateRelayVariables })(
    SharedUserAndRelayVarLoaderComponent
);
